import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import RecordingStatus from '../components/RecordingStatus';
import RetroCassette from '../components/RetroCassette';
import { BIBLE_BOOKS, getVersesForChapter, getVerseText } from '../utils/bibleData';
import { ClipboardDocumentListIcon, BookOpenIcon, EyeIcon, LightBulbIcon, ArrowUpTrayIcon, ArrowDownTrayIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { useTheme } from '../contexts/ThemeContext';
import { useBatchRecording } from '../hooks/useBatchRecording';
import { useVerseSelection } from '../hooks/useVerseSelection';
import { useDraftManagement } from '../hooks/useDraftManagement';
import { useUploadManagement } from '../hooks/useUploadManagement';
import { usePlanManagement } from '../hooks/usePlanManagement';
import { usePassagePlanManagement } from '../hooks/usePassagePlanManagement';
import { formatPassageLabel, computePassagePlanProgress, fetchUserRecordingsForPassagePlan } from '../utils/passagePlans';
import PlanCompletionModal from '../components/PlanCompletionModal';
import ShareCardComposer from '../components/ShareCardComposer';
import ChapterCelebration from '../components/ChapterCelebration';
import { useModalManagement } from '../hooks/useModalManagement';
import { useAudioProcessing } from '../hooks/useAudioProcessing';
import { useScrollManagement } from '../hooks/useScrollManagement';

// Small spinning cassette reel — used as the live "mic is on" indicator in the
// recording controls (drawn as SVG so the spokes/hub stay perfectly centered).
function CassetteReel() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="w-7 h-7 flex-shrink-0 animate-spin"
      style={{ animationDuration: '2.4s', animationTimingFunction: 'linear' }}
      aria-hidden="true"
    >
      {/* reel body: concentric rings */}
      <circle cx="12" cy="12" r="11" fill="#1A5A73" stroke="#1E6B8A" strokeWidth="1" />
      <circle cx="12" cy="12" r="8.5" fill="#247BA0" />
      <circle cx="12" cy="12" r="6" fill="#4A9BC7" />
      {/* three spokes (six visible arms) from the hub */}
      <g stroke="#EAF4F8" strokeWidth="1.6" strokeLinecap="round">
        <line x1="12" y1="3.5" x2="12" y2="20.5" />
        <line x1="4.64" y1="7.75" x2="19.36" y2="16.25" />
        <line x1="4.64" y1="16.25" x2="19.36" y2="7.75" />
      </g>
      {/* center hub */}
      <circle cx="12" cy="12" r="2.6" fill="#063354" stroke="#EAF4F8" strokeWidth="1" />
    </svg>
  );
}

// Review-screen verse audio player. Mobile Safari (Low Data Mode, a
// backgrounded tab, or a stalled connection) can leave a plain <audio
// preload="metadata"> silently stuck forever — no error event, no timeout,
// just a spinner that never resolves. This wraps the element with a timeout
// that surfaces a visible "Tap to load audio" retry, since reloading the src
// inside a real tap satisfies the user-gesture requirement some mobile
// browsers impose before they'll fetch media at all.
function VerseAudioPlayer({ src, type }) {
  const audioRef = useRef(null);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'stuck' | 'error'

  useEffect(() => {
    setStatus('loading');
    const el = audioRef.current;
    if (!el) return;

    const handleReady = () => setStatus('ready');
    const handleError = () => setStatus('error');
    el.addEventListener('loadedmetadata', handleReady);
    el.addEventListener('canplay', handleReady);
    el.addEventListener('error', handleError);

    const timer = setTimeout(() => {
      setStatus(current => (current === 'loading' ? 'stuck' : current));
    }, 6000);

    return () => {
      clearTimeout(timer);
      el.removeEventListener('loadedmetadata', handleReady);
      el.removeEventListener('canplay', handleReady);
      el.removeEventListener('error', handleError);
    };
  }, [src]);

  const retry = () => {
    setStatus('loading');
    const el = audioRef.current;
    if (el) {
      el.load();
      el.play().then(() => el.pause()).catch(() => {});
    }
  };

  return (
    <div>
      <audio
        ref={audioRef}
        key={src}
        controls
        className="w-full h-8"
        preload="metadata"
        crossOrigin="anonymous"
      >
        <source src={src} type={type} />
        Your browser does not support the audio element.
      </audio>
      {(status === 'stuck' || status === 'error') && (
        <button
          type="button"
          onClick={retry}
          className="mt-1.5 flex items-center gap-1.5 text-xs text-cerulean hover:text-space-cadet dark:hover:text-white transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {status === 'error' ? 'Couldn\'t load audio — tap to retry' : 'Taking a while — tap to retry loading'}
        </button>
      )}
    </div>
  );
}

// A bare ?book=&chapter= link (e.g. Dashboard's "Continue Recording" /
// "Pick up where I left off") means "load the whole chapter and scroll to
// the next unrecorded verse" — normal auto-advance should still run. Only a
// full ?book=&chapter=&start=&end= link (e.g. Listen's "Record This
// Passage") means "jump to this exact verse range", which should be treated
// as a manual pick that skips auto-advance.
function isExplicitPassageLink(search) {
  const params = new URLSearchParams(search);
  return !!params.get('book') && (params.has('start') || params.has('end'));
}

function Record() {
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  // Subscribe to the active translation so verse text re-renders on switch.
  const { translation, translations } = useTranslation();
  const { theme } = useTheme();
  const translationName = (translations.find((t) => t.id === translation) || {}).name || '';

  // UI state
  const [existingRecordings, setExistingRecordings] = useState([]);
  // Verses the current user has already recorded in the selected chapter (DB).
  const [recordedForMe, setRecordedForMe] = useState(null);
  // Verse-level totals across the whole passage plan — powers the cassette
  // count when the passage-plan toggle is active.
  const [planVerseCount, setPlanVerseCount] = useState({ recorded: 0, total: 0 });
  // Bumped to force a re-read of recordings (e.g. after a verse is deleted
  // elsewhere and the user returns to this page).
  const [recordingsRefreshTick, setRecordingsRefreshTick] = useState(0);
  // Verse numbers the user deselected in the review modal (won't be uploaded).
  const [excludedVerseNumbers, setExcludedVerseNumbers] = useState(() => new Set());
  const toggleVerseSelection = (verseNumber) => {
    setExcludedVerseNumbers(prev => {
      const next = new Set(prev);
      if (next.has(verseNumber)) next.delete(verseNumber); else next.add(verseNumber);
      return next;
    });
  };
  const [showRecordingStatus, setShowRecordingStatus] = useState(false);
  const verseTextRef = useRef(null);
  // Session-only override for the compact cassette (not persisted — resets on remount).
  // null means "defer to the saved recordingPreferences.cassetteCompact value".
  const [sessionCassetteCompact, setSessionCassetteCompact] = useState(null);
  
  // Mobile interface state
  const [isMobile, setIsMobile] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdownNumber, setCountdownNumber] = useState(3);
  const [isScrollingToRecording, setIsScrollingToRecording] = useState(false);
  const [isInRecordingMode, setIsInRecordingMode] = useState(false);
  // True from mount until the initial auto-advance settles on the real next
  // verse to record — the Record button shows a spinner instead of its dot
  // icon during this window so it doesn't briefly show a stale verse number.
  const [isFindingRecordingSpot, setIsFindingRecordingSpot] = useState(true);
  const initialFindDoneRef = useRef(false);

  // Plan limit state — chapter count for free families
  const [planBlock, setPlanBlock] = useState(null); // null = no block, or { chaptersUsed, limit }

  // Count unique recorded chapters across all of the user's families.
  // Only matters for free-plan users; beta/paid users are always unblocked.
  useEffect(() => {
    if (!currentUser) return;
    const { isBeta, subscriptionPlan, familyIds } = currentUser;
    if (isBeta) { setPlanBlock(null); return; }
    if (subscriptionPlan === 'legacy' || subscriptionPlan === 'plus') { setPlanBlock(null); return; }
    if (!familyIds?.length) { setPlanBlock(null); return; }

    (async () => {
      try {
        const { collection, getDocs, query, where, getDoc, doc } = await import('firebase/firestore');
        const { db: firestoreDb } = await import('../config/firebase');

        // Check if any family has a paid/beta plan — if so, no limit
        for (const fid of familyIds) {
          const fSnap = await getDoc(doc(firestoreDb, 'families', fid));
          if (fSnap.exists()) {
            const p = fSnap.data().plan;
            if (p === 'beta' || p === 'legacy' || p === 'plus') {
              setPlanBlock(null);
              return;
            }
          }
        }

        // All families are free — count unique chapters across all of them
        const uniqueChapters = new Set();
        for (const fid of familyIds) {
          const snap = await getDocs(
            query(collection(firestoreDb, 'recordings'), where('familyIds', 'array-contains', fid))
          );
          snap.docs.forEach(d => {
            const { book, chapter } = d.data();
            if (book && chapter != null) uniqueChapters.add(`${book}_${chapter}`);
          });
        }

        const CHAPTER_LIMIT = 10;
        if (uniqueChapters.size >= CHAPTER_LIMIT) {
          setPlanBlock({ chaptersUsed: uniqueChapters.size, limit: CHAPTER_LIMIT, recordedChapters: uniqueChapters });
        } else {
          setPlanBlock(null);
        }
      } catch (e) {
        console.error('Plan limit check failed:', e);
        setPlanBlock(null); // fail open
      }
    })();
  }, [currentUser]);


  // Social share card ("I just recorded…") — offered from the upload success modal
  const [shareOpen, setShareOpen] = useState(false);
  const [shareContext, setShareContext] = useState(null);

  // Confetti celebration when the upload completes the LAST verse of a chapter
  const [celebration, setCelebration] = useState(null); // { bookName, chapter } | null

  // Use the verse selection hook
  const {
    // Selection state
    selectedBook,
    selectedChapter,
    startVerse,
    endVerse,
    
    // Available options
    availableChapters,
    availableVerses,
    availableEndVerses,
    
    // State setters
    setSelectedBook,
    setSelectedChapter,
    setStartVerse,
    setEndVerse,
    
    // Computed values
    totalVerses,
    passageString
  } = useVerseSelection({
    currentUser,
    existingRecordings,
    onVerseRangeChange: (book, chapter, start, end) => {
      console.log(`📖 Verse range changed: ${book} ${chapter}:${start}-${end}`);
      // Scroll to the first verse of the new range
      setTimeout(() => scrollToCurrentVerse('next', start), 100);
    }
  });

  // Use the batch recording hook
  const {
    // Recording state
    isRecording,
    isPaused,
    recordingTime,
    audioBlob,
    currentVerseIndex,
    
    // Verse management
    verseAudioURLs,
    pendingVerseSaves,
    currentVerseNumber,
    progressPercentage,
    
    // UI state
    showReviewButton,
    
    // Recording controls
    warmUpMic,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    restartCurrentVerse,
    
    // Verse navigation
    nextVerse,
    previousVerse,
    
    // Review functionality
    handleReviewClick,

    // In-modal re-record
    reRecordingVerse,
    reRecordTime,
    reRecordProcessing,
    startReRecord,
    stopReRecord,

    // Utility functions
    formatTime,
    getVersePreview,
    
    // State setters
    setAudioBlob,
    setRecordingTime,
    setCurrentVerseIndex,
    setVerseAudioURLs,
    
    // Cleanup
    cleanup
  } = useBatchRecording({
    selectedBook,
    selectedChapter,
    startVerse,
    endVerse,
    currentUser,
    onVerseComplete: (verse, verseFile) => {
      console.log(`✅ Verse ${verse} completed:`, verseFile);
    },
    onRecordingComplete: () => {
      openSummaryModal();
    },
    onRecordingModeReset: () => {
      setIsInRecordingMode(false);
    }
  });

  // Use the draft management hook
  const {
    // State
    hasDraft,
    showResumeDialog,
    hasUnuploadedRecording,
    
    // Setters
    setHasDraft,
    setShowResumeDialog,
    setHasUnuploadedRecording,
    
    // Functions
    saveRecordingDraftLocal,
    saveRecordingDraft,
    restoreRecordingDraft,
    deleteRecordingDraft
  } = useDraftManagement({
    currentUser,
    audioBlob,
    recordingTime,
    currentVerseIndex,
    selectedBook,
    selectedChapter,
    startVerse,
    endVerse,
    onDraftRestored: (draft) => {
      // This will be called when a draft is restored
      console.log('📝 Draft restored:', draft);
      // The draft restoration is handled automatically by the hook
      // The UI will show the resume dialog
    }
  });

  // Use the upload management hook
  const {
    // State
    isUploading,
    uploadStatus,
    uploadMessage,
    showUploadModal,
    
    // Setters
    setIsUploading,
    setUploadStatus,
    setUploadMessage,
    setShowUploadModal,
    
    // Functions
    handleUpload
  } = useUploadManagement({
    currentUser,
    verseAudioURLs,
    excludedVerseNumbers,
    selectedBook,
    selectedChapter,
    startVerse,
    endVerse,
    onUploadSuccess: async ({ verseDocuments, verseCount, book, chapter, shouldAdvance, chapterCompleted }) => {
      console.log('📤 Upload successful:', { verseCount, book, chapter, chapterCompleted });

      // Snapshot what was just uploaded for the social share card, before the
      // plan advances the selection away from this chapter.
      const uploadedVerses = (verseDocuments || [])
        .map(d => ({ verseNumber: d.verse, text: getVerseText(book, chapter, d.verse) }))
        .filter(v => v.text)
        .sort((a, b) => a.verseNumber - b.verseNumber);
      const bookName = BIBLE_BOOKS.find(b => b.id === book)?.name || book;
      setShareContext({
        bookName,
        chapter,
        verses: uploadedVerses,
      });

      // Every verse in the chapter is now recorded — celebrate!
      if (chapterCompleted) {
        setCelebration({ bookName, chapter });
      }

      // Reset state after successful upload
      setAudioBlob(null);
        setRecordingTime(0);
      setCurrentVerseIndex(0);
      setShowSummaryModal(false);
      setHasUnuploadedRecording(false);
      setVerseAudioURLs({});
      setUserHasManuallyChanged(false); // Reset manual override after successful upload
      
      // Update plan progress and check badges
      try {
      const { db } = await import('../config/firebase');
        await updatePlanProgress(verseDocuments, currentUser.uid, db);
        await updatePassagePlanProgress(currentUser.uid, db);
        await checkAndAwardBadges(currentUser.uid, db);
    } catch (error) {
        console.error('Error updating plan progress or checking badges:', error);
      }
      
      // The cassette refresh happens via a reload when the user dismisses the
      // success modal (Continue Recording) — NOT on a timer, which would kill
      // the modal and the share composer out from under the user.
    },
    onUploadError: (error, errorMessage) => {
      console.error('📤 Upload error:', error);
    },
    onUploadComplete: () => {
      console.log('📤 Upload process completed');
    }
  });

  // Use the plan management hook
  const {
    // State
    currentPlanName,
    userHasManuallyChanged,
    showCompletionModal,
    completedPlanData,
    
    // Setters
    setUserHasManuallyChanged,
    
    // Functions
    checkAndAwardBadges,
    updatePlanProgress,
    advanceToNextVerses,
    peekActiveChapterPlanName,
    handleManualBookChange,
    handleManualChapterChange,
    handleManualStartVerseChange,
    handleManualEndVerseChange,
    handleFollowPlan,
    handleCompletionModalClose,
    handleCompletionModalChooseNewPlan
  } = usePlanManagement({
    currentUser,
    selectedBook,
    selectedChapter,
    startVerse,
    endVerse,
    existingRecordings,
    setSelectedBook,
    setSelectedChapter,
    setStartVerse,
    setEndVerse,
    onPlanAdvancement: (book, chapter, start, end) => {
      if (book === 'navigate' && chapter === 'profile' && start === 'plans') {
        // Navigate to profile page plans section
        navigate('/profile');
        return;
      }
      console.log(`📖 Plan advancement: ${book} ${chapter}:${start}-${end}`);
      // Scroll to the first verse of the new range
      setTimeout(() => scrollToCurrentVerse('next', start), 100);
    }
  });

  // Passages Plans — separate from usePlanManagement above (different
  // Firestore shape/subscription collection), but shares the same manual-
  // override flag so only one plan type ever drives the cassette at a time.
  const {
    currentPassagePlanName,
    passagePlanPassages,
    passagePlanCompletedIndexes,
    passagePlanFullyRecorded,
    currentPassageIndex,
    advanceToNextPassage,
    updatePassagePlanProgress,
    jumpToPassage,
    resumeFollowingPassagePlan,
  } = usePassagePlanManagement({
    currentUser,
    userHasManuallyChanged,
    setUserHasManuallyChanged,
    setSelectedBook,
    setSelectedChapter,
    setStartVerse,
    setEndVerse,
    onPassageAdvancement: (book, chapter, start) => {
      setTimeout(() => scrollToCurrentVerse('next', start), 100);
    }
  });

  // Which plan's name the header title shows when the user has both a
  // Chapter Plan and a Passages Plan subscription — tapping the title toggles
  // between them (mirrors the mobile app's Record screen). advanceToNextPassage
  // and advanceToNextVerses each already trigger their own scroll-to-verse via
  // onPassageAdvancement/onPlanAdvancement above, so switching plans here
  // scrolls to the next verse to record for free.
  const [activePlanDisplay, setActivePlanDisplay] = useState('passage');
  const handlePlanToggle = async () => {
    if (!currentPassagePlanName || !currentPlanName) return;
    const next = activePlanDisplay === 'passage' ? 'chapter' : 'passage';
    setActivePlanDisplay(next);
    if (next === 'passage') {
      await resumeFollowingPassagePlan();
    } else {
      await handleFollowPlan();
    }
  };

  // Suppresses the "Manually Selected Passage" override banner when the
  // current selection is actually the Passages Plan's current step (e.g.
  // arriving via a "Start Recording" link that points at the same passage
  // the plan would've auto-advanced to anyway) — a real manual override is
  // picking something other than the plan's current passage.
  const isOnCurrentPassagePlanStep = passagePlanPassages.length > 0 &&
    currentPassageIndex !== null &&
    passagePlanPassages[currentPassageIndex]?.book === selectedBook &&
    Number(passagePlanPassages[currentPassageIndex]?.chapter) === Number(selectedChapter) &&
    Number(passagePlanPassages[currentPassageIndex]?.startVerse) === Number(startVerse) &&
    Number(passagePlanPassages[currentPassageIndex]?.endVerse) === Number(endVerse);

  // Honor ?book=&chapter= links. A bare book+chapter (e.g. Dashboard's
  // "Continue Recording") just pre-selects the chapter and lets the normal
  // auto-advance effect below expand it to the full chapter and scroll to
  // the next unrecorded verse. A full ?book=&chapter=&start=&end= link (e.g.
  // Listen's "Record This Passage" button) jumps straight to that exact
  // verse range and is marked manual so auto-advance doesn't override it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const book = params.get('book');
    if (!book) return;
    const chapter = parseInt(params.get('chapter'), 10) || 1;
    setSelectedBook(book);
    setSelectedChapter(chapter);
    if (isExplicitPassageLink(window.location.search)) {
      const start = parseInt(params.get('start'), 10) || 1;
      const end = parseInt(params.get('end'), 10) || start;
      setStartVerse(start);
      setEndVerse(end);
      setUserHasManuallyChanged(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use the modal management hook
  const {
    // Modal state
    showSummaryModal,
    showDeleteConfirm,
    showInstructionsTooltip,
    showSmartSelectionInfo,
    
    // Modal setters
    setShowSummaryModal,
    setShowSmartSelectionInfo,
    
    // Modal handlers
    openSummaryModal,
    closeSummaryModal,
    openDeleteConfirm,
    closeDeleteConfirm,
    toggleInstructionsTooltip,
    openSmartSelectionInfo,
    
    // Modal action handlers
    handleDeleteRecording,
    handleStartFresh,
    handleResumeRecording,
    handleUploadRetry,
    handleContinueRecording
  } = useModalManagement({
    onModalAction: {
      handleDeleteRecording: async () => {
        // Reset all recording state
        setAudioBlob(null);
        setRecordingTime(0);
        setCurrentVerseIndex(0);
        setVerseAudioURLs({});
        setHasUnuploadedRecording(false);
        setUserHasManuallyChanged(false);
        closeSummaryModal();
      },
      handleStartFresh: async () => {
        await deleteRecordingDraft();
        setShowResumeDialog(false);
        setHasUnuploadedRecording(false);
      },
      handleResumeRecording: async () => {
        await restoreRecordingDraft();
        setShowSummaryModal(true);
        setShowResumeDialog(false);
      },
      handleUploadRetry: () => {
        setShowUploadModal(false);
        // Reset to allow retry
        setAudioBlob(null);
        setRecordingTime(0);
        setCurrentVerseIndex(0);
        setVerseAudioURLs({});
        setHasUnuploadedRecording(false);
      },
      handleContinueRecording: () => {
        setShowUploadModal(false);
        // Refresh so the cassette picks up the next selection (this reload
        // used to run on a 1.5s timer right after upload, which yanked the
        // success modal and share composer away mid-use).
        window.location.reload();
      }
    }
  });

  // Use the audio processing hook
  const {
    // Helper functions
    getPassageString,
    
    // Audio processing functions
    generateVerseAudioFiles,
    
    // Data getters
    getVerseCount,
    getSortedVerseData
  } = useAudioProcessing({
    verseAudioURLs,
    selectedBook,
    selectedChapter,
    startVerse,
    endVerse
  });

  // Use the scroll management hook
  const {
    // State
    isControlsSticky,
    
    // Functions
    scrollToTop,
    scrollToCurrentVerse,
    
    // Class name getters
    getStickyControlsClass,
    getControlsContainerClass,
    getProgressBarClass
  } = useScrollManagement({
    startVerse,
    currentVerseIndex,
    verseTextRef
  });

  // Scroll to top when page loads with URL parameters to ensure cassette is visible
  useEffect(() => {
    const cleanup = scrollToTop();
    return cleanup;
  }, [selectedBook, selectedChapter, startVerse, endVerse, scrollToTop]);

  // Re-read recordings when the user returns to this page/tab, so counts and
  // the recorded-verse dimming reflect verses deleted elsewhere.
  useEffect(() => {
    const refresh = () => setRecordingsRefreshTick(t => t + 1);
    const onVisibility = () => { if (!document.hidden) refresh(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Fetch the current user's existing recordings for the selected book/chapter.
  // Personal-only: everyone records their own copy of any verse (no family
  // gap-filling). The verse list still lets the user record any verse.
  useEffect(() => {
    const fetchExistingRecordings = async () => {
      if (!selectedBook || !selectedChapter || !currentUser) return;

      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const { db } = await import('../config/firebase');

        const book = selectedBook.toLowerCase();
        const q = query(
          collection(db, 'recordings'),
          where('book', '==', book),
          where('chapter', '==', selectedChapter),
          where('userId', '==', currentUser.uid)
        );
        const querySnapshot = await getDocs(q);
        setExistingRecordings(querySnapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error('Error fetching existing recordings:', error);
        setExistingRecordings([]);
      }
    };

    fetchExistingRecordings();
  }, [selectedBook, selectedChapter, currentUser, recordingsRefreshTick]);

  // Count the verses the current user has recorded in the selected chapter
  // (shown on the cassette). Personal-only — no family tally.
  useEffect(() => {
    const computeRecorded = async () => {
      if (!selectedBook || !selectedChapter || !currentUser) {
        setRecordedForMe(null);
        return;
      }
      try {
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const { db } = await import('../config/firebase');
        const book = selectedBook.toLowerCase();

        const mineSnap = await getDocs(query(
          collection(db, 'recordings'),
          where('book', '==', book),
          where('chapter', '==', selectedChapter),
          where('userId', '==', currentUser.uid)
        ));
        const mine = new Set();
        mineSnap.docs.forEach(d => { const v = d.data().verse; if (v) mine.add(Number(v)); });
        setRecordedForMe(mine.size);
      } catch (e) {
        console.error('Error computing recorded verses:', e);
      }
    };
    computeRecorded();
  }, [selectedBook, selectedChapter, currentUser, existingRecordings, recordingsRefreshTick]);

  // Verse-level progress across the whole passage plan — used for the cassette
  // count when the passage-plan toggle is active. Recomputes whenever the plan
  // or the user's recordings change (recordingsRefreshTick fires after upload).
  useEffect(() => {
    let cancelled = false;
    const computePlanVerses = async () => {
      if (!currentUser || !passagePlanPassages || passagePlanPassages.length === 0) {
        setPlanVerseCount({ recorded: 0, total: 0 });
        return;
      }
      try {
        const userRecs = await fetchUserRecordingsForPassagePlan(currentUser.uid);
        if (cancelled) return;
        const prog = computePassagePlanProgress(passagePlanPassages, userRecs);
        setPlanVerseCount({ recorded: prog.recordedVerses, total: prog.totalVerses });
      } catch (e) {
        console.error('Error computing passage-plan verse count:', e);
      }
    };
    computePlanVerses();
    return () => { cancelled = true; };
  }, [currentUser, passagePlanPassages, existingRecordings, recordingsRefreshTick]);

  // Auto-advance to next verses when recordings are loaded (only if user hasn't
  // manually changed, and not when a ?book= link chose the passage explicitly).
  // A Passages Plan subscription takes priority if active; only fall back to
  // Reading Plan / default whole-Bible advancement if there isn't one.
  //
  // Once the user has both plan types, this effect re-runs on every chapter
  // switch (changing selectedChapter re-fetches existingRecordings, whose
  // length is a dependency below) — so without the activePlanDisplay guard,
  // toggling to the Chapter Plan would immediately get overridden right back
  // to the Passages Plan on the very next re-fetch, since it always wins the
  // priority race. Respect whichever plan the user explicitly chose instead.
  useEffect(() => {
    const cameFromPassageLink = isExplicitPassageLink(window.location.search);
    if (currentUser && existingRecordings.length >= 0 && !userHasManuallyChanged && !cameFromPassageLink) {
      // Small delay to ensure all state is properly set
      setTimeout(async () => {
        if (activePlanDisplay === 'chapter') {
          advanceToNextVerses(true);
          return;
        }
        const handledByPassagePlan = await advanceToNextPassage();
        if (!handledByPassagePlan) {
          advanceToNextVerses(true); // Use existing data for page load
        } else {
          // Passages Plan is driving the page, but the user may *also* have
          // a Chapter Plan — look up its name (without navigating to it) so
          // the header's plan-switch toggle knows to appear.
          peekActiveChapterPlanName();
        }
      }, 500);
    }
  }, [currentUser, existingRecordings.length, userHasManuallyChanged, activePlanDisplay]);

  // Spinner-only tracking for the Record button while the page is finding the
  // next verse to record on load. Deliberately kept fully separate from the
  // advancement effect above (own timer, no cleanup that could cancel or
  // delay it) so this display concern can never affect real navigation.
  useEffect(() => {
    if (initialFindDoneRef.current || authLoading) return;
    const cameFromPassageLink = isExplicitPassageLink(window.location.search);
    if (!currentUser || userHasManuallyChanged || cameFromPassageLink) {
      initialFindDoneRef.current = true;
      setIsFindingRecordingSpot(false);
      return;
    }
    // Roughly mirrors the advancement effect's 500ms delay plus its fetch time.
    const timer = setTimeout(() => {
      initialFindDoneRef.current = true;
      setIsFindingRecordingSpot(false);
    }, 1200);
    return () => clearTimeout(timer);
  }, [authLoading, currentUser, userHasManuallyChanged, existingRecordings.length]);



  // Generate verse audio URLs when modal opens
  useEffect(() => {
    if (showSummaryModal) {
      // Always generate files when modal opens, regardless of current state
      console.log('🎵 Modal opened, starting file generation...');
      generateVerseAudioFiles();
      // Start each review with every verse selected for upload
      setExcludedVerseNumbers(new Set());
    }
  }, [showSummaryModal]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Countdown logic
  useEffect(() => {
    if (showCountdown && countdownNumber > 0) {
      const timer = setTimeout(() => {
        setCountdownNumber(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showCountdown && countdownNumber === 0) {
      // Countdown finished, start actual recording
      // Don't hide countdown immediately - let startRecording handle the transition
      setCountdownNumber(3);
      setIsScrollingToRecording(false);
      // Call the actual startRecording function
      startRecording();
    }
  }, [showCountdown, countdownNumber, startRecording]);

  // Hide countdown when recording actually starts
  useEffect(() => {
    if (isRecording && showCountdown) {
      setShowCountdown(false);
    }
  }, [isRecording, showCountdown]);

  // Auto-scroll to current verse when verse changes (mobile only)
  useEffect(() => {
    if (isMobile && (isInRecordingMode || isRecording || isPaused || showCountdown || isScrollingToRecording) && verseTextRef.current) {
      const currentVerseElement = verseTextRef.current.querySelector(`[data-verse="${currentVerseNumber}"]`);
      if (currentVerseElement) {
        // Small delay to ensure the verse element is rendered
        setTimeout(() => {
          currentVerseElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [currentVerseNumber, isMobile, isRecording, isPaused, showCountdown, isScrollingToRecording]);








  // saveCurrentVerseFile is now provided by the useBatchRecording hook

  // Custom startRecording function for mobile interface
  const handleStartRecording = () => {
    // Enforce plan chapter limit for free families.
    // Re-recording an already-recorded chapter is always allowed.
    if (planBlock) {
      const chapterKey = `${selectedBook}_${selectedChapter}`;
      const alreadyRecorded = planBlock.recordedChapters?.has(chapterKey);
      if (!alreadyRecorded) {
        alert(
          `Your family has recorded ${planBlock.chaptersUsed} of ${planBlock.limit} chapters included in the Free Plan.\n\n` +
          `Upgrade at lineagebible.com to record the full Bible.`
        );
        return;
      }
    }

    // Warm up the mic immediately (during the countdown on mobile) so the first
    // verse is captured on a settled mic rather than a cold one.
    warmUpMic?.();
    if (isMobile) {
      // Mobile: Start countdown and scroll animations
      setShowCountdown(true);
      setCountdownNumber(3);
      setIsScrollingToRecording(true);
      setIsInRecordingMode(true);
      console.log('🎬 MOBILE RECORDING: isScrollingToRecording set to true');
      
      // Force the entire controls section (including progress bar) to be sticky immediately on mobile
      const controlsContainer = document.querySelector('.transition-all.duration-200');
      if (controlsContainer) {
        controlsContainer.style.position = 'fixed';
        controlsContainer.style.top = '0';
        controlsContainer.style.left = '0';
        controlsContainer.style.right = '0';
        controlsContainer.style.zIndex = '50';
        // Inline styles override the dark: CSS rules, so pick the color here
        const isDarkMode = document.documentElement.classList.contains('dark');
        controlsContainer.style.backgroundColor = isDarkMode ? '#0d1520' : 'white';
        controlsContainer.style.borderBottom = isDarkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e5e7eb';
        controlsContainer.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
        console.log('🎬 MOBILE RECORDING: Forced entire controls section to be sticky');
      }
      
      // Also remove the cassette element from DOM to eliminate any space
      const cassetteElement = document.querySelector('[data-cassette="true"]');
      if (cassetteElement) {
        cassetteElement.style.display = 'none';
        console.log('🎬 MOBILE RECORDING: Hidden cassette element');
      }
      
      // Scroll cassette out of view and controls to top
      setTimeout(() => {
        const cassetteElement = document.querySelector('[data-cassette="true"]');
        const controlsElement = document.getElementById('recording-controls');
        const verseTextElement = verseTextRef.current;
        
        if (cassetteElement) {
          cassetteElement.style.transform = 'translateY(-100vh)';
          cassetteElement.style.transition = 'transform 0.8s ease-out';
        }
        
        if (controlsElement) {
          controlsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        
        if (verseTextElement) {
          // Scroll the first verse into view within the text container
          const firstVerse = verseTextElement.querySelector('[data-verse]');
          if (firstVerse) {
            firstVerse.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            verseTextElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 100);
    } else {
      // Desktop: give the warmed mic a brief moment to settle, then start
      setTimeout(() => startRecording(), 300);
    }
  };

  // Verses recorded this session (saved to temp storage, not yet uploaded).
  // Counting them acknowledges a verse as soon as it's recorded, without
  // waiting for Save & Upload.
  const sessionRecordedVerses = Object.keys(verseAudioURLs).map(k => Number(k.replace('verse-', '')));
  const myUploadedVerses = new Set(
    (existingRecordings || [])
      .filter(r => r.userId === currentUser?.uid)
      .map(r => Number(r.verse))
  );
  // Session captures not already in the DB — counted immediately so the tally
  // reflects this-session recordings before upload (mirrors mobile's doneSet).
  const sessionNewCount = sessionRecordedVerses.filter(v => !myUploadedVerses.has(v)).length;

  // Cassette "X of Y verses recorded": whole passage plan when the passage
  // toggle is active, otherwise the current chapter (DB + this-session captures).
  const showPlanCount = activePlanDisplay === 'passage' && passagePlanPassages.length > 0;
  const chapterRecordedCount = recordedForMe == null ? null : recordedForMe + sessionNewCount;
  const chapterTotalVerses = selectedBook && selectedChapter
    ? getVersesForChapter(selectedBook, selectedChapter).length
    : null;
  const cassetteRecorded = showPlanCount ? planVerseCount.recorded : chapterRecordedCount;
  const cassetteTotal = showPlanCount ? planVerseCount.total : chapterTotalVerses;

  return (
    <div className={`w-full pt-0 pb-0 lg:py-8 bg-white dark:bg-[#0d1520] ${isScrollingToRecording ? 'mobile-recording-mode' : ''}`} style={{ scrollBehavior: 'smooth', minHeight: '100vh', paddingBottom: '80px' }}>
      {/* Mobile Countdown Overlay */}
      {showCountdown && isMobile && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="text-8xl font-bold text-white mb-4 animate-pulse">
              {countdownNumber}
            </div>
            <div className="text-xl text-white">
              {countdownNumber > 1 ? 'Get ready...' : 'Recording starts now!'}
            </div>
          </div>
        </div>
      )}

      {/* Mobile-specific styles */}
      <style jsx>{`
        @media (max-width: 767px) {
          [data-cassette="true"] {
            transition: transform 0.8s ease-out;
          }
          
          .mobile-recording-mode {
            padding-top: 0;
          }
          
          .mobile-recording-mode [data-cassette="true"] {
            display: none !important;
          }
          
          .mobile-recording-mode .mobile-recording-text-position {
            position: fixed !important;
            top: 140px !important; /* Position below recording controls + progress bar */
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important; /* Extend to bottom of viewport */
            margin: 0 !important;
            padding: 1rem !important;
            padding-bottom: 7rem !important; /* Extra padding to clear bottom navigation */
            z-index: 40 !important;
            background: white !important;
            overflow-y: auto !important; /* Allow scrolling within the text area */
          }

          .dark .mobile-recording-mode .mobile-recording-text-position {
            background: #0d1520 !important;
          }

          /* Make progress bar part of the sticky controls area */
          .mobile-recording-mode .grid {
            margin-bottom: 0 !important;
            margin-top: 0 !important;
            position: static !important;
            background: white !important;
            padding: 0.75rem 1rem !important;
          }

          .dark .mobile-recording-mode .grid {
            background: #0d1520 !important;
          }
          
          /* Remove spacing between recording controls and progress bar */
          .mobile-recording-mode #recording-controls + .grid {
            margin-top: 0 !important;
          }
          
          /* Ensure controls and progress bar are positioned at the very top */
          .mobile-recording-mode #recording-controls {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
          }
          
          /* Override any spacing from the sticky controls container */
          .mobile-recording-mode .transition-all {
            margin: 0 !important;
            padding: 0 !important;
          }
          
          /* Force the entire controls section to be at the top */
          .mobile-recording-mode .transition-all.duration-200 {
            top: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>
      {/* Desktop Header - hidden on mobile */}
      <div className="hidden lg:flex justify-between items-center mb-8">
        {currentPassagePlanName || currentPlanName ? (
          <button
            type="button"
            onClick={handlePlanToggle}
            disabled={!(currentPassagePlanName && currentPlanName)}
            className="flex items-center gap-1.5 pl-4 text-3xl font-bold text-cerulean dark:text-aquamarine disabled:cursor-default"
            title={currentPassagePlanName && currentPlanName ? 'Switch between your Chapter Plan and Passage Plan' : undefined}
          >
            {activePlanDisplay === 'passage' && currentPassagePlanName
              ? currentPassagePlanName
              : activePlanDisplay === 'chapter' && currentPlanName
              ? currentPlanName
              : currentPassagePlanName || currentPlanName}
            {currentPassagePlanName && currentPlanName && (
              <ChevronDownIcon className="w-6 h-6 flex-shrink-0" />
            )}
          </button>
        ) : (
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white pl-4">Record a Passage</h1>
        )}
        <div className="flex items-center gap-3">
          <button 
            onClick={toggleInstructionsTooltip}
            className="text-cerulean hover:text-primary-700 flex items-center gap-1 text-sm pr-4"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Instructions
          </button>
        </div>
      </div>

      {/* Consolidated Instructions Tooltip */}
      {showInstructionsTooltip && (
        <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-400/10 border border-blue-200 dark:border-blue-400/25 rounded-[3px] lg:mx-4">
          <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-3">Here's how it works 🎙️</h4>

          {/* General Recording Instructions */}
          <div className="mb-4">
            <h5 className="text-blue-800 dark:text-blue-300 font-medium text-sm mb-2 flex items-center gap-1.5"><ClipboardDocumentListIcon className="w-4 h-4" /> The simple version:</h5>
            <ul className="text-blue-700 dark:text-blue-300/80 text-sm space-y-1 mb-3">
              <li>• Press Record, and read the verse highlighted in blue — just {endVerse - startVerse + 1} verse{endVerse - startVerse === 0 ? '' : 's'} in this passage</li>
              <li>• When you finish a verse, tap "Next Verse" and keep reading</li>
              <li>• That's it! Each verse is saved on its own, so your family can listen verse by verse</li>
              <li>• Flub a word? No worries — tap Redo and read it again</li>
              <li>• When you're done, tap Review to hear your takes before anything is shared</li>
            </ul>
          </div>

          {/* Current Verse Instructions */}
          {(isRecording || isPaused) && (
            <div className="mb-4">
              <h5 className="text-blue-800 dark:text-blue-300 font-medium text-sm mb-2 flex items-center gap-1.5"><BookOpenIcon className="w-4 h-4" /> Right now:</h5>
              <ul className="text-blue-700 dark:text-blue-300/80 text-sm space-y-1 mb-3">
                <li>• You're on verse {startVerse + currentVerseIndex} — it's the one glowing blue below</li>
                <li>• {isPaused
                  ? "You're paused. Press Resume when you're ready to pick back up"
                  : 'Read it in your own voice, at your own pace, then tap "Next Verse"'
                }</li>
              </ul>
            </div>
          )}

          {/* Verse Highlighting Instructions */}
          {(isRecording || isPaused) && (
            <div className="mb-4">
              <h5 className="text-blue-800 dark:text-blue-300 font-medium text-sm mb-2 flex items-center gap-1.5"><EyeIcon className="w-4 h-4" /> Finding your way:</h5>
              <ul className="text-blue-700 dark:text-blue-300/80 text-sm space-y-1 mb-3">
                <li>• The blue highlight always shows the verse to read next</li>
                <li>• Prev and Next let you move between verses anytime</li>
                <li>• The progress bar shows how far you've come</li>
              </ul>
            </div>
          )}

          {/* Recording Tips */}
          <div>
            <h5 className="text-blue-800 dark:text-blue-300 font-medium text-sm mb-2 flex items-center gap-1.5"><LightBulbIcon className="w-4 h-4" /> A few friendly tips:</h5>
            <ul className="text-blue-700 dark:text-blue-300/80 text-sm space-y-1">
              <li>• Find a cozy, quiet spot — a closet full of clothes is secretly a great recording studio</li>
              <li>• Read like you're reading to someone you love, because you are</li>
              <li>• Take a breath between verses; there's no rush</li>
              <li>• Long chapter? Record a few verses now and come back later — your progress is saved</li>
              <li>• Don't chase perfect. Your real voice, pauses and all, is the keepsake</li>
            </ul>
          </div>
        </div>
      )}
      
      {/* Plan Override Notice and Follow Plan Button */}
      {userHasManuallyChanged && !isOnCurrentPassagePlanStep && (
        <div className="mx-4 mb-4 p-3 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-[3px]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-white/60">
                Manually Selected Passage
              </span>
            </div>
            <button
              onClick={async () => {
                // Whichever plan type (Reading or Passages) is actually
                // active resets its own plan-name state; the inactive one
                // harmlessly finds no subscription and clears to null.
                await resumeFollowingPassagePlan();
                await handleFollowPlan();
              }}
              className="px-3 py-1 bg-gray-500 hover:bg-gray-600 text-white text-sm font-medium rounded-[3px] transition-colors"
            >
              Back to Plan
            </button>
          </div>
        </div>
      )}

      {/* Passages Plan panel — passage list with click-to-jump/re-record.
          Hidden while the header toggle is showing the Chapter Plan instead. */}
      {passagePlanPassages.length > 0 && activePlanDisplay !== 'chapter' && (
        <div className="mx-4 mb-4 p-3 bg-cerulean/5 border border-cerulean/20 rounded-[3px]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-cerulean">{currentPassagePlanName}</span>
            <span className="text-xs text-gray-500 dark:text-white/50">
              {passagePlanCompletedIndexes.size}/{passagePlanPassages.length} recorded
            </span>
          </div>
          {passagePlanFullyRecorded && (
            <p className="text-xs text-green-600 dark:text-green-400 mb-2">
              All passages in this plan are recorded! Tap any one below to re-record it.
            </p>
          )}
          <div className="flex flex-wrap gap-1.5">
            {passagePlanPassages.map((p, i) => {
              const isDone = passagePlanCompletedIndexes.has(i);
              const isCurrent = currentPassageIndex === i;
              return (
                <button
                  key={i}
                  onClick={() => jumpToPassage(p, i)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    isCurrent
                      ? 'bg-cerulean text-white border-cerulean'
                      : isDone
                        ? 'bg-green-100 dark:bg-green-400/15 text-green-800 dark:text-green-400 border-green-200 dark:border-green-400/25 hover:bg-green-200 dark:hover:bg-green-400/25'
                        : 'bg-white dark:bg-white/5 text-gray-600 dark:text-white/60 border-gray-200 dark:border-white/15 hover:bg-gray-50 dark:hover:bg-white/10'
                  }`}
                  title={isDone ? 'Recorded — tap to re-record' : 'Not yet recorded'}
                >
                  {formatPassageLabel(p)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Free plan chapter usage indicator */}
      {planBlock && (
        <div className="mx-1.5 mt-1.5 px-4 py-2.5 rounded-[3px] bg-amber-50 dark:bg-amber-400/10 border border-amber-200 dark:border-amber-400/25 flex items-center justify-between gap-3">
          <div>
            <span className="text-xs font-semibold text-amber-800 dark:text-amber-300">
              {planBlock.chaptersUsed} / {planBlock.limit} chapters recorded
            </span>
            <span className="text-xs text-amber-600 dark:text-amber-400 ml-1">· Free Plan limit reached</span>
          </div>
          <a
            href="https://lineagebible.com/pricing.html"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs font-semibold text-cerulean hover:underline whitespace-nowrap"
          >
            Upgrade →
          </a>
        </div>
      )}

      {/* Retro Cassette Tape - Beautiful visual interface */}
      {currentUser?.recordingPreferences?.showCassette !== false && (() => {
        const effectiveCassetteCompact = sessionCassetteCompact ?? (currentUser?.recordingPreferences?.cassetteCompact || false);
        return (
        <div data-cassette="true" className="pr-1.5 pt-1.5 pb-0 pl-1.5 relative">
          <RetroCassette
            compact={effectiveCassetteCompact}
            title={passageString}
            isRecording={isRecording}
            isPaused={isPaused}
            onRecordClick={!isRecording && !isPaused ? handleStartRecording : isPaused ? resumeRecording : pauseRecording}
            onStopClick={stopRecording}
            showControls={false}
            // Verse selection props
            selectedBook={selectedBook}
            selectedChapter={selectedChapter}
            startVerse={startVerse}
            endVerse={endVerse}
            availableBooks={BIBLE_BOOKS}
            availableChapters={availableChapters}
            availableVerses={availableVerses}
            availableEndVerses={availableEndVerses}
            onBookChange={handleManualBookChange}
            onChapterChange={handleManualChapterChange}
            onStartVerseChange={handleManualStartVerseChange}
            onEndVerseChange={handleManualEndVerseChange}
            // Timer prop
            recordingTime={recordingTime}
            // Plan name prop
            planName={currentPassagePlanName || currentPlanName}
            // "X of Y verses recorded" — chapter or whole plan (session-recorded
            // verses count immediately, before upload, for the chapter scope)
            recordedCount={cassetteRecorded}
            totalVerses={cassetteTotal}
            // Active translation (shown as microtext under the reference line)
            translationName={translationName}
          />
          <button
            type="button"
            onClick={() => setSessionCassetteCompact(!effectiveCassetteCompact)}
            title={effectiveCassetteCompact ? 'Show full cassette' : 'Show compact cassette'}
            className="absolute right-3 top-1.5 z-10 w-6 h-6 flex items-center justify-center rounded-full bg-black/20 hover:bg-black/35 text-white/90"
          >
            <svg className={`w-3.5 h-3.5 transition-transform ${effectiveCassetteCompact ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        );
      })()}

      {/* Recording Controls - Mobile and Desktop */}
      <div className={isMobile && (isInRecordingMode || isRecording || isPaused || showCountdown || isScrollingToRecording) && !showSummaryModal && !showDeleteConfirm && !showResumeDialog && !showUploadModal
        ? 'fixed top-0 left-0 right-0 z-50 bg-white dark:bg-[#0d1520] border-b border-gray-200 dark:border-white/10 shadow-sm transition-all duration-200'
        : getStickyControlsClass()}>
        {/* Main recording button and verse navigation on same line */}
        <div className={isMobile && (isInRecordingMode || isRecording || isPaused || showCountdown || isScrollingToRecording) && !showSummaryModal && !showDeleteConfirm && !showResumeDialog && !showUploadModal
          ? 'flex gap-2 sm:gap-3 lg:gap-4 items-center justify-center px-4 py-3'
          : getControlsContainerClass()} id="recording-controls">
          {(isRecording || isPaused) ? (
            // Recording/paused — order: [Review|Live wheels] · Prev · Redo · Pause/Resume
            <>
              {isPaused ? (
                <button
                  onClick={handleReviewClick}
                  className="h-12 px-2 bg-gradient-to-r from-cerulean to-aqua-deep hover:from-cerulean/90 hover:to-aqua-deep/90 text-white font-semibold rounded-[3px] shadow-lg flex items-center justify-center gap-1 transition-all duration-200 text-sm flex-1"
                >
                  <ArrowUpTrayIcon className="w-5 h-5 text-white" />
                  <span className="hidden sm:inline">Review ({getVerseCount() + 1})</span>
                  <span className="sm:hidden">Review</span>
                </button>
              ) : (
                // Live indicator: the cassette reels keep spinning here so you
                // can see the mic is live even though the cassette scrolled away.
                <div
                  className="h-12 flex-1 bg-gray-200 dark:bg-white/10 rounded-[3px] shadow-lg flex items-center justify-center gap-1 px-1 overflow-hidden animate-subtle-breathe"
                  aria-label="Recording — live"
                  title="Recording — live"
                >
                  <CassetteReel />
                  <div className="w-2.5 h-1 bg-gray-400 rounded-full flex-shrink-0"></div>
                  <CassetteReel />
                </div>
              )}
              <button
                type="button"
                onClick={previousVerse}
                disabled={currentVerseIndex === 0}
                className="h-12 px-2 bg-slate-600 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-[3px] shadow-lg flex items-center justify-center gap-1 transition-all duration-200 text-sm flex-1"
              >
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z"/>
                </svg>
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">Prev</span>
              </button>
              <button
                onClick={restartCurrentVerse}
                className="h-12 px-2 bg-slate-500 hover:bg-slate-600 text-white font-semibold rounded-[3px] shadow-lg flex items-center justify-center gap-1 transition-all duration-200 text-sm flex-1"
              >
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
                </svg>
                <span className="hidden sm:inline">Redo</span>
                <span className="sm:hidden">Redo</span>
              </button>
              {isPaused ? (
                <button
                  onClick={resumeRecording}
                  className="h-12 px-2 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-[3px] shadow-lg flex items-center justify-center gap-1 transition-all duration-200 text-sm flex-1"
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                  <span className="hidden sm:inline">Resume</span>
                  <span className="sm:hidden">Resume</span>
                </button>
              ) : (
                <button
                  onClick={pauseRecording}
                  className="h-12 px-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-[3px] shadow-lg flex items-center justify-center gap-1 transition-all duration-200 text-sm flex-1"
                  style={{
                    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                    boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.6), 0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 19H10V5H6V19ZM14 5V19H18V5H14Z"/>
                  </svg>
                  <span className="hidden sm:inline">Pause</span>
                  <span className="sm:hidden">Pause</span>
                </button>
              )}
            </>
          ) : (
            // Idle — Record (plus Review on desktop)
            <>
              {!isMobile && (
                <button
                  onClick={handleReviewClick}
                  className="h-12 px-4 bg-gradient-to-r from-cerulean to-aqua-deep hover:from-cerulean/90 hover:to-aqua-deep/90 text-white font-semibold rounded-[3px] shadow-lg flex items-center justify-center gap-2 transition-all duration-200 text-sm"
                >
                  <ArrowUpTrayIcon className="w-5 h-5 text-white" />
                  <span className="hidden sm:inline">Review ({getVerseCount()})</span>
                  <span className="sm:hidden">Review</span>
                </button>
              )}
              <button
                onClick={handleStartRecording}
                disabled={isFindingRecordingSpot}
                className="h-12 px-4 bg-coral hover:bg-red-600 disabled:opacity-80 text-white font-semibold rounded-[3px] shadow-lg flex items-center justify-center gap-2 transition-all duration-200 text-sm flex-1"
              >
                {isFindingRecordingSpot ? (
                  <div className="w-5 h-5 rounded-full border-2 border-white/40 border-t-white animate-spin flex-shrink-0" />
                ) : (
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/>
                  </svg>
                )}
                <span className="hidden sm:inline">Record Verse {startVerse + currentVerseIndex}</span>
                <span className="sm:hidden">Record</span>
              </button>
            </>
          )}
        </div>
        
        {/* Progress Bar and Review Button - Always show during mobile recording, conditional for desktop */}
        <div className={isMobile && (isInRecordingMode || isRecording || isPaused || showCountdown || isScrollingToRecording) 
          ? `grid gap-3 mb-3 px-4 grid-cols-4` 
          : (isRecording || isPaused || showCountdown) ? getProgressBarClass() : 'hidden'}>
          {/* Progress Indicator - Left side, 75% width (3/4) */}
          <div className="col-span-3">
            <div className="bg-gray-50 dark:bg-white/5 rounded-[3px] p-3 border border-gray-200 dark:border-white/10">
              <div className="text-xs text-gray-600 dark:text-white/60 mb-2 font-medium">
                {isMobile ? (
                  `${selectedBook.charAt(0).toUpperCase() + selectedBook.slice(1)} ${selectedChapter}: Verse ${startVerse}-${endVerse}`
                ) : (
                  `Progress - Verse ${currentVerseNumber} towards ${endVerse}`
                )}
              </div>
              <div className="w-full bg-gray-200 dark:bg-white/10 rounded-full h-2.5 mb-1">
                <div 
                  className="bg-cerulean h-2.5 rounded-full transition-all duration-300 shadow-sm"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
          </div>
          
          {/* Next Verse Button - Right side, 25% width (1/4) */}
          <div className="col-span-1">
              <div className="flex justify-center h-full">
                <button
                  type="button"
                  onClick={nextVerse}
                  disabled={currentVerseIndex >= (endVerse - startVerse)}
                  className={`h-12 px-3 font-semibold rounded-[3px] shadow-lg transition-all duration-200 text-sm flex items-center justify-center gap-1 w-full ${
                    currentVerseIndex >= (endVerse - startVerse)
                      ? 'bg-gray-200 dark:bg-white/10 opacity-50 cursor-not-allowed text-gray-500 dark:text-white/40'
                      : 'bg-gradient-to-r from-cerulean to-aqua-deep hover:from-cerulean/90 hover:to-aqua-deep/90 text-white'
                  }`}
                >
                  {currentVerseIndex >= (endVerse - startVerse) ? (
                    <>
                      <svg className="w-4 h-4 text-gray-400 dark:text-white/35" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z"/>
                      </svg>
                      <span className="hidden sm:inline">All Done</span>
                      <span className="sm:hidden">Done</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8.59 16.59L10 18L16 12L10 6L8.59 7.41L13.17 12L8.59 16.59Z"/>
                      </svg>
                      <span className="hidden sm:inline">Next Verse</span>
                      <span className="sm:hidden">Next</span>
                    </>
                  )}
                </button>
              </div>
            </div>
        </div>
      </div>
        
        <div 
          className={`bg-white dark:bg-[#111827] p-4 sm:p-6 rounded-[3px] mb-6 lg:mt-0 ${isScrollingToRecording ? 'mobile-recording-text-position' : 'mt-4'}`} 
          ref={verseTextRef}
          data-debug-classes={`isScrollingToRecording: ${isScrollingToRecording}, classes: ${isScrollingToRecording ? 'mobile-recording-text-position' : 'mt-4'}`}
          data-debug-mobile={`isMobile: ${isMobile}, showCountdown: ${showCountdown}`}
          style={isMobile && (isInRecordingMode || isRecording || isPaused || showCountdown || isScrollingToRecording) ? {
            position: 'fixed',
            top: '158px',
            left: '0',
            right: '0',
            bottom: '0',
            margin: '0',
            padding: '1rem',
            paddingBottom: '7rem', // Extra padding to clear bottom navigation (112px + some buffer)
            zIndex: '40',
            // Inline style outranks the dark: CSS rules — pick per theme here
            backgroundColor: theme === 'dark' ? '#0d1520' : 'white',
            overflowY: 'auto'
          } : isMobile ? {
            // Mobile: Add extra top margin to account for cassette height
            marginTop: '2rem'
          } : {}}
        >
            <div className="text-gray-900 dark:text-white text-lg leading-relaxed">
            {(() => {
              // Verses already recorded (per the personal/family recording scope)
              // are rendered a few shades lighter so the still-to-record verses
              // stand out — "bold in reverse". Verses recorded THIS session
              // (saved to temp storage but not yet uploaded) count too, so the
              // page acknowledges a verse the moment its recording is saved.
              const recordedSet = new Set([
                ...(existingRecordings || []).map(r => Number(r.verse)),
                ...Object.keys(verseAudioURLs).map(k => Number(k.replace('verse-', ''))),
              ]);
              const versesPerBlock = Number(currentUser?.recordVersesPerBlock) || 3;
              if (startVerse && endVerse && startVerse !== endVerse) {
                // Group verses in blocks per the user's verses-per-block preference
                const verses = [];
                for (let v = startVerse; v <= endVerse; v++) {
                  verses.push({
                    number: v,
                    text: getVerseText(selectedBook, selectedChapter, v)
                  });
                }
                
                return verses.map((verse, index) => {
                  const isCurrentVerse = verse.number === currentVerseNumber;
                  
                  return (
                    <span key={verse.number} data-verse={verse.number}>
                      <span className={`font-semibold ${isCurrentVerse ? 'text-space-cadet dark:text-white' : recordedSet.has(verse.number) ? 'text-gray-300 dark:text-white/25' : 'text-gray-400 dark:text-white/35'}`}>
                        {verse.number}.
                      </span>
                      <span className={`${isCurrentVerse ? 'bg-mint-green dark:bg-cerulean text-space-cadet dark:text-white px-2 py-1 rounded-[3px] font-medium' : recordedSet.has(verse.number) ? 'text-gray-400 dark:text-white/35' : 'text-gray-800 dark:text-white/90'}`}>
                        {verse.text.replace(/^["']|["']$/g, '')}
                      </span>
                      {/* Add space between verses within a block; line break after every Nth verse (user preference) */}
                      {(index + 1) % versesPerBlock !== 0 && index < verses.length - 1 && ' '}
                      {(index + 1) % versesPerBlock === 0 && index < verses.length - 1 && (
                        <div className="h-4"></div>
                      )}
                    </span>
                  );
                });
              } else if (startVerse) {
                // Single verse
                const isCurrentVerse = startVerse === currentVerseNumber;
                
                return (
                  <div data-verse={startVerse}>
                    <span className={`font-semibold ${isCurrentVerse ? 'text-space-cadet dark:text-white' : recordedSet.has(startVerse) ? 'text-gray-300 dark:text-white/25' : 'text-gray-400 dark:text-white/35'}`}>
                      {startVerse}.
                    </span>
                    <span className={`${isCurrentVerse ? 'bg-mint-green dark:bg-cerulean text-space-cadet dark:text-white px-2 py-1 rounded-[3px] font-medium' : recordedSet.has(startVerse) ? 'text-gray-400 dark:text-white/35' : 'text-gray-800 dark:text-white/90'}`}>
                      {getVerseText(selectedBook, selectedChapter, startVerse).replace(/^["']|["']$/g, '')}
                    </span>
                  </div>
                );
              } else {
                // All verses in chapter
                const verses = getVersesForChapter(selectedBook, selectedChapter);
                return verses.map((verse, index) => {
                  const isCurrentVerse = verse === currentVerseNumber;
                  
                  return (
                    <span key={verse} data-verse={verse}>
                      <span className={`font-semibold ${isCurrentVerse ? 'text-space-cadet dark:text-white' : recordedSet.has(verse) ? 'text-gray-300 dark:text-white/25' : 'text-gray-400 dark:text-white/35'}`}>
                        {verse}.
                      </span>
                      <span className={`${isCurrentVerse ? 'bg-mint-green dark:bg-cerulean text-space-cadet dark:text-white px-2 py-1 rounded-[3px] font-medium' : recordedSet.has(verse) ? 'text-gray-400 dark:text-white/35' : 'text-gray-800 dark:text-white/90'}`}>
                        {getVerseText(selectedBook, selectedChapter, verse).replace(/^["']|["']$/g, '')}
                      </span>
                      {/* Add space between verses within a block; line break after every Nth verse (user preference) */}
                      {(index + 1) % versesPerBlock !== 0 && index < verses.length - 1 && ' '}
                      {(index + 1) % versesPerBlock === 0 && index < verses.length - 1 && (
                        <div className="h-4"></div>
                      )}
                    </span>
                  );
                });
              }
            })()}
          </div>
        </div>

      {/* Recording Status Summary - Always Visible */}
      <div className="card mb-4 pb-20">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="grid grid-cols-3 gap-4 sm:gap-6 w-full sm:w-auto">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {totalVerses}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-white/60">Total Verses</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                {(() => {
                  const recordedVerses = new Set();
                  existingRecordings.forEach(recording => {
                    // Handle both old format (startVerse/endVerse) and new format (verse)
                    if (recording.verse) {
                      // New format: single verse - check if it's in our range
                      if (recording.verse >= startVerse && recording.verse <= endVerse) {
                        recordedVerses.add(recording.verse);
                      }
                    } else if (recording.startVerse) {
                      // Old format: verse range
                      for (let v = Math.max(recording.startVerse, startVerse); v <= Math.min(recording.endVerse || recording.startVerse, endVerse); v++) {
                        recordedVerses.add(v);
                      }
                    }
                  });
                  return recordedVerses.size;
                })()}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-white/60">Already Recorded</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-orange-600">
                {(() => {
                  const recordedVerses = new Set();
                  existingRecordings.forEach(recording => {
                    // Handle both old format (startVerse/endVerse) and new format (verse)
                    if (recording.verse) {
                      // New format: single verse - check if it's in our range
                      if (recording.verse >= startVerse && recording.verse <= endVerse) {
                        recordedVerses.add(recording.verse);
                      }
                    } else if (recording.startVerse) {
                      // Old format: verse range
                      for (let v = Math.max(recording.startVerse, startVerse); v <= Math.min(recording.endVerse || recording.startVerse, endVerse); v++) {
                        recordedVerses.add(v);
                      }
                    }
                  });
                  const totalVerses = endVerse - startVerse + 1;
                  return totalVerses - recordedVerses.size;
                })()}
              </div>
              <div className="text-xs sm:text-sm text-gray-600 dark:text-white/60">Remaining</div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowSmartSelectionInfo(!showSmartSelectionInfo)}
              className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 flex items-center gap-1 text-sm"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Smart Selection
            </button>
            <button
              onClick={() => setShowRecordingStatus(!showRecordingStatus)}
              className="flex items-center gap-2 text-sm text-gray-600 dark:text-white/60 hover:text-gray-800 dark:hover:text-white transition-colors"
            >
              <span>{showRecordingStatus ? 'Hide' : 'Show'} Details</span>
              <svg 
                className={`h-4 w-4 transition-transform ${showRecordingStatus ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Smart Selection Accordion - Expanded with data display */}
        {showSmartSelectionInfo && (
          <div className="w-full mt-4 p-4 bg-green-50 dark:bg-green-400/10 border border-green-200 dark:border-green-400/25 rounded-[3px]">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h4 className="font-medium text-green-900 dark:text-green-300 mb-2">Smart Selection Rules</h4>
                <div className="space-y-2 text-sm text-green-800 dark:text-green-300">
                  <p>
                    <span className="font-medium">Start Verse:</span> Automatically set to next unrecorded verse ({startVerse})
                  </p>
                  <p>
                    <span className="font-medium">End Verse:</span> Set to complete the chapter ({endVerse})
                  </p>
                  <p>
                    <span className="font-medium">Current Status:</span> {(() => {
                      const recordedVerses = new Set();
                      existingRecordings.forEach(recording => {
                        // Handle both old format (startVerse/endVerse) and new format (verse)
                        if (recording.verse) {
                          // New format: single verse
                          recordedVerses.add(recording.verse);
                        } else if (recording.startVerse) {
                          // Old format: verse range
                          for (let v = recording.startVerse; v <= (recording.endVerse || recording.startVerse); v++) {
                            recordedVerses.add(v);
                          }
                        }
                      });
                      const totalVerses = getVersesForChapter(selectedBook, selectedChapter).length;
                      const recordedCount = recordedVerses.size;
                      return `${recordedCount} of ${totalVerses} verses already recorded`;
                    })()}
                  </p>
                  <div className="mt-3 p-2 bg-white dark:bg-white/5 rounded border border-green-200 dark:border-green-400/25 dark:border-green-400/25">
                    <p className="text-xs text-green-700 dark:text-green-300/80">
                      <span className="font-medium">How it works:</span> The system automatically finds the next unrecorded verse in your selected chapter and sets the start point there, ensuring you never record the same verse twice.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Recording Status Details - Accordion */}
      {showRecordingStatus && (
        <div className="card mb-4">
          <RecordingStatus 
          book={selectedBook}
          chapter={selectedChapter}
          startVerse={startVerse}
          endVerse={endVerse}
        />
      </div>
      )}
      


      {/* Recording Summary Modal */}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-[#111827] rounded-[3px] max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-timberwolf bg-space-cadet rounded-t-lg">
              <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-white">Recording Summary</h2>
              </div>
              <button
                onClick={closeSummaryModal}
                className="text-white hover:text-aquamarine transition-colors"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Recording Info */}
              <div className="mb-6">
                <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-[3px] p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-space-cadet dark:text-white mb-2">
                        {getPassageString(selectedBook, selectedChapter, startVerse, endVerse)}
                      </h3>
                      <p className="text-space-cadet dark:text-white text-sm">
                        Individual Verse Files: {getVerseCount()}
                      </p>
                      <p className="text-space-cadet dark:text-white text-xs mt-1">
                        {getVerseCount() > 0 ? 
                          `Verses: ${getSortedVerseData().map(v => v.verseNumber).join(', ')}` : 
                          'No verse files recorded yet'
                        }
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-cerulean">
                        {getVerseCount()}
                      </div>
                      <div className="text-xs text-cerulean">Verses Recorded</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Verse List */}
              <div className="mb-6">
                <div className="space-y-3">
                  {/* Show individual verse files */}
                  {(getVerseCount() > 0 || Object.keys(pendingVerseSaves).length > 0) ? (
                    <>
                    {getSortedVerseData().map((verseData) => {
                      const isIncluded = !excludedVerseNumbers.has(verseData.verseNumber);
                      return (
                        <div key={verseData.verseNumber} className={`p-3 border rounded-[3px] transition-colors ${isIncluded ? 'bg-cerulean bg-opacity-10 border-cerulean' : 'bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={isIncluded}
                              onChange={() => toggleVerseSelection(verseData.verseNumber)}
                              className="mt-1 h-5 w-5 rounded border-gray-300 dark:border-white/15 text-cerulean focus:ring-cerulean cursor-pointer flex-shrink-0"
                              title={isIncluded ? "Uncheck to skip this verse" : "Check to include this verse"}
                            />
                            <div className={isIncluded ? '' : 'opacity-50'}>
                              <div className="font-medium text-space-cadet dark:text-white">
                                Verse {verseData.verseNumber}
                                {!isIncluded && <span className="ml-2 text-xs text-gray-500 dark:text-white/40 font-normal">won't upload</span>}
                              </div>
                              <div className="text-sm text-space-cadet dark:text-white text-opacity-70 mt-1 leading-snug">
                                {getVerseText(selectedBook, selectedChapter, verseData.verseNumber)?.replace(/^["']|["']$/g, '')}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                              <div className="text-sm font-medium text-cerulean">{formatTime(verseData.duration)}</div>
                          </div>
                        </div>

                        {/* Individual audio player for this verse */}
                        <div className={`mt-2 ${isIncluded ? '' : 'opacity-40'}`}>
                          <div className="text-xs text-space-cadet dark:text-white text-opacity-60 mb-1">
                            Duration: {formatTime(verseData.duration)}
                          </div>
                          <VerseAudioPlayer
                            src={verseData.downloadURL}
                            type={
                              verseData.filename?.endsWith('.webm') ? 'audio/webm'
                              : verseData.filename?.endsWith('.m4a') ? 'audio/mp4'
                              : verseData.filename?.endsWith('.mp3') ? 'audio/mpeg'
                              : verseData.filename?.endsWith('.ogg') ? 'audio/ogg'
                              : 'audio/wav'
                            }
                          />
                        </div>

                        {/* Inline re-record controls */}
                        {reRecordProcessing === verseData.verseNumber ? (
                          <div className="mt-3 flex items-center gap-2 text-sm text-cerulean">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cerulean" />
                            <span>Saving new recording…</span>
                          </div>
                        ) : reRecordingVerse === verseData.verseNumber ? (
                          <div className="mt-3 flex items-center gap-3 bg-red-50 dark:bg-red-400/10 border border-red-200 dark:border-red-400/25 rounded-[3px] px-3 py-2">
                            <div className="flex items-center gap-2 flex-1">
                              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                              <span className="text-sm font-mono text-red-700 dark:text-red-300/80">{formatTime(reRecordTime)}</span>
                              <span className="text-xs text-red-500 dark:text-red-400">Recording…</span>
                            </div>
                            <button
                              onClick={async () => {
                                await stopReRecord();
                                setExcludedVerseNumbers(prev => {
                                  const next = new Set(prev);
                                  next.delete(verseData.verseNumber);
                                  return next;
                                });
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-[3px] transition-colors"
                            >
                              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                <rect x="6" y="6" width="12" height="12" rx="1" />
                              </svg>
                              Stop
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startReRecord(verseData.verseNumber)}
                            disabled={!!reRecordingVerse || !!reRecordProcessing}
                            className="mt-3 flex items-center gap-2 text-xs text-cerulean hover:text-space-cadet dark:hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                            </svg>
                            Re-record this verse
                          </button>
                        )}
                      </div>
                      );
                    })}
                    {/* Verses still encoding/uploading in the background */}
                    {Object.keys(pendingVerseSaves)
                      .map(Number)
                      .filter(n => !verseAudioURLs[`verse-${n}`])
                      .sort((a, b) => a - b)
                      .map(n => (
                        <div key={`pending-${n}`} className="p-3 border border-cerulean/40 bg-cerulean/5 dark:bg-cerulean/10 rounded-[3px] flex items-center gap-3">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-cerulean border-t-transparent flex-shrink-0" />
                          <div>
                            <div className="font-medium text-space-cadet dark:text-white">Verse {n}</div>
                            <div className="text-xs text-gray-500 dark:text-white/50">Processing your recording…</div>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-white/40">
                      <p>No verse files recorded yet.</p>
                              </div>
                  )}

                            </div>
                          </div>

              {/* Audio Preview - Removed since we now have individual verse files */}
            </div>

            {/* Modal Footer */}
            <div className="flex flex-col sm:flex-row items-center justify-between p-4 sm:p-6 pb-24 sm:pb-20 border-t border-timberwolf dark:border-white/10 bg-timberwolf dark:bg-white/5 gap-3 sm:gap-0">
              <button
                onClick={() => {
                  setShowSummaryModal(false);
                  // Re-check recorded verses/counts so the page acknowledges
                  // what was just recorded (dimmed verses, cassette tallies).
                  setRecordingsRefreshTick(t => t + 1);
                }}
                className="px-6 py-2 text-space-cadet dark:text-white hover:text-cerulean transition-colors order-2 sm:order-1"
              >
                Close & Continue
              </button>
              <div className="flex flex-col sm:flex-row gap-3 order-1 sm:order-2 w-full sm:w-auto">
                <button
                  onClick={openDeleteConfirm}
                  className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-[3px] font-semibold transition-all duration-200 w-full sm:w-auto"
                >
                  Delete & Redo
                </button>
                <button
                  onClick={handleUpload}
                  disabled={isUploading || Object.keys(pendingVerseSaves).length > 0 || (getVerseCount() - excludedVerseNumbers.size) === 0}
                  className="btn-primary px-8 py-2 bg-cerulean hover:bg-space-cadet text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-[3px] font-semibold transition-all duration-200 w-full sm:w-auto min-w-[200px]"
                >
                  {/* key forces a clean text-node swap instead of an in-place
                      content update — on iOS Safari, changing a button's label
                      length right as it's tapped can paint the old and new
                      text on top of each other for a frame. */}
                  <span key={isUploading ? 'uploading' : Object.keys(pendingVerseSaves).length > 0 ? 'processing' : 'ready'}>
                    {isUploading
                      ? 'Uploading...'
                      : Object.keys(pendingVerseSaves).length > 0
                      ? 'Processing…'
                      : `Save & Upload (${getVerseCount() - excludedVerseNumbers.size})`}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white dark:bg-[#111827] rounded-[3px] max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Dialog Header */}
            <div className="flex items-center justify-between p-4 border-b border-timberwolf bg-space-cadet rounded-t-lg">
              <h2 className="text-lg font-bold text-white">Confirm Deletion</h2>
              <button
                onClick={closeDeleteConfirm}
                className="text-white hover:text-aquamarine transition-colors"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Dialog Content */}
            <div className="p-4">
              <div className="mb-4">
                <h3 className="text-base font-semibold text-space-cadet dark:text-white mb-3">What would you like to do?</h3>
                
                <div className="space-y-3">
                  {/* Close & Continue */}
                  <div className="flex items-start gap-2 p-2 bg-aquamarine bg-opacity-20 border border-aquamarine rounded-[3px]">
                    <div className="w-5 h-5 bg-aquamarine rounded-full flex items-center justify-center text-space-cadet dark:text-white text-xs font-bold mt-0.5 flex-shrink-0">
                      ✓
                    </div>
                    <div>
                      <div className="font-medium text-space-cadet dark:text-white text-sm">Close & Continue</div>
                      <div className="text-xs text-space-cadet dark:text-white text-opacity-70">
                        Keep your recording and return to the main page. You can review or upload it later.
                      </div>
                    </div>
                  </div>

                  {/* Save & Upload */}
                  <div className="flex items-start gap-2 p-2 bg-cerulean bg-opacity-20 border border-cerulean rounded-[3px]">
                    <div className="w-5 h-5 bg-cerulean rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5 flex-shrink-0">
                      ↑
                    </div>
                    <div>
                      <div className="font-medium text-space-cadet dark:text-white text-sm">Save & Upload</div>
                      <div className="text-xs text-space-cadet dark:text-white text-opacity-70">
                        Save your recording to the database and make it available to your family.
                      </div>
                    </div>
                  </div>

                  {/* Delete & Redo */}
                  <div className="flex items-start gap-2 p-2 bg-coral bg-opacity-20 border border-coral rounded-[3px]">
                    <div className="w-5 h-5 bg-coral rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5 flex-shrink-0">
                      ⚠
                    </div>
                    <div>
                      <div className="font-medium text-space-cadet dark:text-white text-sm">Delete & Redo</div>
                      <div className="text-xs text-space-cadet dark:text-white text-opacity-70">
                        <strong>Permanently delete</strong> this recording and start over. This cannot be undone.
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center text-xs text-space-cadet dark:text-white text-opacity-70 mb-4">
                Are you sure you want to delete this recording?
              </div>
            </div>

            {/* Dialog Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-timberwolf bg-timberwolf rounded-b-lg">
              <button
                onClick={closeDeleteConfirm}
                className="px-4 py-2 text-space-cadet dark:text-white hover:text-cerulean transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteRecording}
                className="px-4 py-2 bg-coral hover:bg-red-600 text-white rounded-[3px] font-semibold transition-all duration-200 text-sm"
              >
                Yes, Delete Recording
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume Recording Dialog */}
      {showResumeDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
          <div className="bg-white dark:bg-[#111827] rounded-[3px] max-w-md w-full">
            {/* Dialog Header */}
            <div className="flex items-center justify-between p-6 border-b border-timberwolf bg-space-cadet rounded-t-lg">
              <h2 className="text-xl font-bold text-white">Resume Recording?</h2>
              <button
                onClick={() => setShowResumeDialog(false)}
                className="text-white hover:text-aquamarine transition-colors"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Dialog Content */}
            <div className="p-6">
              <div className="mb-6">
                <p className="text-space-cadet dark:text-white mb-4">
                  We found an unfinished recording from your previous session. Would you like to resume where you left off?
                </p>
                
                <div className="bg-aquamarine bg-opacity-20 border border-aquamarine rounded-[3px] p-4">
                  <div className="text-sm text-space-cadet dark:text-white">
                    <strong>Recording Details:</strong>
                    <br />
                    • {getPassageString(selectedBook, selectedChapter, startVerse, endVerse)}
                    <br />
                    • {endVerse - startVerse + 1} verses recorded
                    <br />
                    • {formatTime(recordingTime)} total time
                  </div>
                </div>
              </div>
            </div>

            {/* Dialog Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-timberwolf bg-timberwolf rounded-b-lg">
              <button
                onClick={handleStartFresh}
                className="px-6 py-2 text-space-cadet dark:text-white hover:text-cerulean transition-colors"
              >
                Start Fresh
              </button>
              <button
                onClick={handleResumeRecording}
                className="px-6 py-2 bg-cerulean hover:bg-space-cadet text-white rounded-[3px] font-semibold transition-all duration-200"
              >
                Resume Recording
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Success/Error Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-[#111827] rounded-[3px] max-w-md w-full shadow-2xl">
            {/* Modal Content */}
            <div className="px-6 py-6 text-center">
              <h3 className={`text-xl font-semibold mb-3 ${
                uploadStatus === 'success' ? 'text-aqua-deep dark:text-aquamarine' : 'text-red-700 dark:text-red-400'
              }`}>
                {uploadStatus === 'success' ? 'Upload Successful!' : 'Upload Failed'}
              </h3>

              <p className="text-base mb-6 text-space-cadet dark:text-white/80">
                {uploadMessage}
              </p>

              {uploadStatus === 'success' && (
                <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-[3px] p-4 mb-6">
                  <div className="flex items-center justify-center space-x-2 text-aqua-deep dark:text-aquamarine">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium">Your recordings are now available to your family!</span>
                  </div>
                </div>
              )}

              {uploadStatus === 'success' && shareContext?.verses?.length > 0 && (
                <div className="rounded-2xl border border-cerulean/20 bg-cerulean/5 dark:bg-white/5 p-4 mb-6">
                  {/* Social destinations — purely iconographic, the button below does the work */}
                  <div className="flex items-center justify-center gap-4 mb-3">
                    <div className="w-9 h-9 rounded-full bg-white dark:bg-white/10 border border-gray-200 dark:border-white/15 flex items-center justify-center text-space-cadet/70 dark:text-white/70" title="Instagram">
                      <svg className="w-[18px] h-[18px]" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                        <rect x="3" y="3" width="18" height="18" rx="5" />
                        <circle cx="12" cy="12" r="4" />
                        <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" stroke="none" />
                      </svg>
                    </div>
                    <div className="w-9 h-9 rounded-full bg-white dark:bg-white/10 border border-gray-200 dark:border-white/15 flex items-center justify-center text-space-cadet/70 dark:text-white/70" title="Facebook">
                      <svg className="w-[18px] h-[18px]" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M13.5 21v-7.5h2.5l.4-3H13.5V8.4c0-.87.24-1.46 1.5-1.46h1.6V4.34C16.3 4.24 15.3 4.14 14.2 4.14c-2.3 0-3.9 1.4-3.9 4v2.36H7.8v3h2.5V21h3.2z" />
                      </svg>
                    </div>
                    <div className="w-9 h-9 rounded-full bg-white dark:bg-white/10 border border-gray-200 dark:border-white/15 flex items-center justify-center text-space-cadet/70 dark:text-white/70" title="Save to device">
                      <ArrowDownTrayIcon className="w-[18px] h-[18px]" />
                    </div>
                  </div>
                  <button
                    onClick={() => setShareOpen(true)}
                    className="w-full px-4 py-3 rounded-[3px] font-semibold bg-gradient-to-r from-cerulean to-aqua-deep hover:from-cerulean/90 hover:to-aqua-deep/90 text-white transition-all duration-200 flex items-center justify-center gap-2 shadow-md"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z" />
                    </svg>
                    Create Art and Share!
                  </button>
                </div>
              )}

              {uploadStatus === 'error' && (
                <div className="bg-red-50 dark:bg-red-400/10 border border-red-200 dark:border-red-400/25 rounded-[3px] p-4 mb-6">
                  <div className="text-sm text-red-800 dark:text-red-300">
                    <p className="font-medium mb-2">What you can do:</p>
                    <ul className="text-left space-y-1">
                      <li>• Check your internet connection</li>
                      <li>• Try uploading again</li>
                      <li>• Start over with a new recording</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-white/5 rounded-b-2xl">
              <div className="flex space-x-3">
                {uploadStatus === 'error' && (
                  <button
                    onClick={handleUploadRetry}
                    className="flex-1 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-[3px] font-medium transition-colors duration-200"
                  >
                    Start Over
                  </button>
                )}
                <button
                  onClick={handleContinueRecording}
                  className={`flex-1 px-4 py-2 rounded-[3px] font-medium transition-colors duration-200 ${
                    uploadStatus === 'success'
                      ? 'border border-cerulean/40 text-cerulean hover:bg-cerulean/5'
                      : 'bg-gradient-to-r from-cerulean to-aqua-deep hover:from-cerulean/90 hover:to-aqua-deep/90 text-white'
                  }`}
                >
                  {uploadStatus === 'success' ? 'Continue Recording' : 'Try Again'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Social share card composer */}
      {shareContext && (
        <ShareCardComposer
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          verses={shareContext.verses}
          bookName={shareContext.bookName}
          chapter={shareContext.chapter}
        />
      )}

      {/* Confetti celebration — every verse in the chapter just got recorded */}
      <ChapterCelebration
        open={!!celebration}
        bookName={celebration?.bookName}
        chapter={celebration?.chapter}
        onClose={() => setCelebration(null)}
      />

      {/* Plan Completion Modal */}
      <PlanCompletionModal
        isOpen={showCompletionModal}
        onClose={handleCompletionModalClose}
        onChooseNewPlan={handleCompletionModalChooseNewPlan}
        completedPlanData={completedPlanData}
      />
    </div>
  );
}

export default Record; 